import { Link } from "react-router-dom";
import { Mail, Phone, ArrowRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { FOUNDERS, BRAND, IMAGES } from "@/lib/constants";

export default function About() {
  return (
    <>
      <section data-testid="about-hero" className="bg-[#0A1128] py-24 text-white sm:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
            About PrimeCore
          </p>
          <h1 className="mt-4 max-w-4xl font-serif-display text-4xl font-medium leading-[1.05] sm:text-6xl lg:text-7xl">
            From field-tested experience to future-ready quality intelligence.
          </h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/80 sm:text-lg">
            {BRAND.name} was founded by a team of industry veterans with over 70
            years of combined QA &amp; IT expertise — shaped at a premier Sri
            Lankan apparel exporter with a broad global manufacturing footprint.
            Q-Wis is the distillation of decades of hands-on experience into an
            innovative, scalable, efficient solution.
          </p>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Our Story"
                title="Built by people who lived these problems."
                lede="We've worked across diverse product categories, innovative IT environments, and the QA programs of world-renowned brands. Q-Wis is built so manufacturers can pursue operational excellence and digital transformation in quality management — without the typical software burden."
              />
              <div className="mt-10 grid grid-cols-2 gap-6">
                <div>
                  <p className="font-serif-display text-5xl text-[#E8772E]">70+</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#475569]">
                    Years of combined QA &amp; IT expertise
                  </p>
                </div>
                <div>
                  <p className="font-serif-display text-5xl text-[#E8772E]">2</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#475569]">
                    Registered offices — Sri Lanka &amp; Bangladesh
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="overflow-hidden rounded-lg">
                <img src={IMAGES.textile} alt="Textile manufacturing" className="h-[480px] w-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="founders-section" className="bg-[#F8FAFC] py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Founders"
            title="The veterans behind Q-Wis."
            lede="Three industry leaders who have distilled decades of hands-on experience into a practical, high-impact product."
          />

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 pc-stagger">
            {FOUNDERS.map((f) => (
              <div
                key={f.name}
                data-testid={`founder-card-${f.name.split(" ")[0].toLowerCase()}`}
                className="flex flex-col rounded-lg border border-[#E2E8F0] bg-white p-7"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-md bg-[#0A1128] font-serif-display text-2xl text-white">
                  {f.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                </div>
                <h3 className="mt-6 font-serif-display text-2xl leading-tight text-[#0A1128]">
                  {f.name}
                </h3>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#E8772E]">
                  {f.role}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-[#475569]">{f.bio}</p>
                <div className="mt-6 space-y-2 border-t border-[#E2E8F0] pt-5 text-sm text-[#334155]">
                  {f.email && (
                    <a
                      href={`mailto:${f.email}`}
                      className="flex items-center gap-2 hover:text-[#E8772E]"
                    >
                      <Mail size={14} className="text-[#E8772E]" /> {f.email}
                    </a>
                  )}
                  {f.phone && (
                    <a
                      href={`tel:${f.phone.replace(/\s|\(|\)/g, "")}`}
                      className="flex items-center gap-2 hover:text-[#E8772E]"
                    >
                      <Phone size={14} className="text-[#E8772E]" /> {f.phone}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg bg-[#0A1128] p-10 text-white sm:p-16">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
              Work with us
            </p>
            <h2 className="mt-4 max-w-3xl font-serif-display text-3xl leading-tight sm:text-5xl">
              Ready to see how Q-Wis fits your operation?
            </h2>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/contact?intent=demo"
                data-testid="about-cta-demo"
                className="inline-flex items-center gap-2 rounded-md bg-[#E8772E] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#D66826]"
              >
                Request a Demo <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="about-cta-contact"
                className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-transparent px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/5"
              >
                Talk to Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
